import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, merge, of, Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {RequestsService} from '../../../requests/services/requests.service';
import {SubstituteService} from '../../../shared/services/substitute.service';
import {UserService} from '../../../shared/services/user.service';
import {UtilsService} from '../../../shared/services/utils.service';
import {EditSubstituteData} from './edit-substitute.types';

@Component({
  selector: 'app-edit-substitute',
  templateUrl: './edit-substitute.component.html',
  styleUrls: ['./edit-substitute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditSubstituteComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private loadingSubject = new BehaviorSubject(false);
  private errorSubject = new BehaviorSubject(null);
  public form: FormGroup;
  loading$ = this.loadingSubject.asObservable();
  error$ = this.errorSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    private fb: FormBuilder,
    private requestService: RequestsService,
    private dialogRef: MatDialogRef<EditSubstituteComponent>,
    private substituteService: SubstituteService,
    private userService: UserService,
    private utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data: EditSubstituteData
  ) {
    this.form = (substitute => {
      const form = this.fb.group({
        is_permanent: this.fb.control(substitute.endDate === null, []),
        startDate: this.fb.control(substitute.startDate, [Validators.required]),
        endDate: this.fb.control(substitute.endDate, [
          (ac: AbstractControl) => {
            if (!ac.parent?.get('is_permanent')?.value) {
              return Validators.required(ac);
            }
            return null;
          }
        ]),
        user: this.fb.control(substitute.user, [Validators.required]),
        substituteUser: this.fb.control(substitute.substituteUser, [Validators.required])
      });
      form.get('user').disable();
      form.get('substituteUser').disable();
      return form;
    })(this.data.substitute);
  }

  ngOnInit(): void {
    const user = this.form.get('user');
    user.disable();
    merge(this.form.get('is_permanent').valueChanges, of(this.form.get('is_permanent').value))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(isPermanent => {
        if (isPermanent) {
          this.form.get('endDate').reset();
          this.form.get('endDate').disable();
        } else {
          this.form.get('endDate').enable();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onSubmit(): void {
    this.loadingSubject.next(true);
    this.substituteService
      .edit$(this.data.scope, this.form.get('user').value?.userName, this.form.get('substituteUser').value?.userName, {
        endDate: this.form.get('endDate').value,
        startDate: this.form.get('startDate').value
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        response => {
          this.loadingSubject.next(false);
          this.dialogRef.close(response);
        },
        error => {
          this.loadingSubject.next(false);
          this.errorSubject.next(error.error.message);
        }
      );
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
